import React from 'react'
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import styles from './NotificationModal.module.scss'

export type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'confirm'

interface NotificationModalProps {
  isOpen: boolean
  type: NotificationType
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  onConfirm?: () => void
  onCancel?: () => void
  onClose: () => void
  isLoading?: boolean
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  type,
  title,
  message,
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  onClose,
  isLoading = false,
}) => {
  if (!isOpen) return null

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={24} />
      case 'error':
        return <AlertCircle size={24} />
      case 'warning':
      case 'confirm':
        return <AlertTriangle size={24} />
      case 'info':
      default:
        return <Info size={24} />
    }
  }

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm()
    } else {
      onClose()
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    onClose()
  }

  const isConfirmType = type === 'confirm'

  return (
    <div className={styles.overlay} onClick={handleCancel}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={handleCancel}>
          <X size={18} />
        </button>

        <div className={`${styles.iconWrapper} ${styles[type]}`}>{getIcon()}</div>

        <h3 className={styles.title}>{title}</h3>
        <p className={styles.message}>{message}</p>

        <div className={styles.actions}>
          {isConfirmType && (
            <button className={styles.cancelBtn} onClick={handleCancel} disabled={isLoading}>
              {cancelLabel}
            </button>
          )}
          <button className={`${styles.confirmBtn} ${styles[type]}`} onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NotificationModal
